import { Fragment } from 'react'
import type { UnfInvoiceCandidateDto, UnfInvoiceLineKind } from '@crm/shared'
import { Badge } from '../../../shared/ui/Badge'
import { UnfInvoiceLineChoice, type LineChoice } from './UnfInvoiceLineChoice'

const money = (v: number | null): string =>
  v == null
    ? '—'
    : v.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 4 }) + ' ₽'
const qty = (v: number | null): string => (v == null ? '—' : v.toLocaleString('ru-RU'))

const KIND: Record<UnfInvoiceLineKind, { label: string; color: 'green' | 'yellow' | 'blue' }> = {
  MATCH: { label: 'Совпало', color: 'green' },
  CHANGED: { label: 'Станет как в счёте', color: 'yellow' },
  NEW: { label: 'Нет в заявке', color: 'blue' },
}

/** «Было → стало», если значение в счёте отличается от заявки. */
function Change({
  from,
  to,
  fmt,
}: {
  from: number | null
  to: number
  fmt: (v: number | null) => string
}) {
  if (from == null || Math.abs(from - to) <= 0.011) return <span>{fmt(to)}</span>
  return (
    <span>
      <span className="text-gray-400 line-through">{fmt(from)}</span>{' '}
      <span className="font-medium text-gray-900">{fmt(to)}</span>
    </span>
  )
}

/**
 * Сравнение счёта 1С с заявкой (21.10.2026: заказ подстраивается под счёт): что совпало,
 * что станет как в счёте, а для строк «нет в заявке» — как их записать.
 */
export function UnfInvoiceDiff({
  candidate,
  choices,
  disabled,
  onChoice,
}: {
  candidate: UnfInvoiceCandidateDto
  choices: Record<number, LineChoice>
  disabled: boolean
  onChoice: (no: number, c: LineChoice) => void
}) {
  if (candidate.onlyNumber) {
    return (
      <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
        Заказ по этой заявке уже оплачен или закрыт — состав не меняется. В счёт заказа запишутся
        только номер и дата счёта из 1С, если счёт совпадает с заказом на 100%.
      </p>
    )
  }
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="text-left text-xs uppercase text-gray-500">
          <tr>
            <th className="py-1 pr-3">№</th>
            <th className="py-1 pr-3">Товар в счёте</th>
            <th className="py-1 pr-3">Позиция заявки</th>
            <th className="py-1 pr-3 text-right">Кол-во</th>
            <th className="py-1 pr-3 text-right">Цена</th>
            <th className="py-1 pr-3 text-right">Сумма</th>
            <th className="py-1" />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {candidate.lines.map((l) => {
            const guessed = l.matchedBy === 'POSITION' && !l.linked
            const editable = l.kind === 'NEW' || l.linked || guessed
            const badge = guessed
              ? { label: 'Похоже: то же кол-во и цена', color: 'yellow' as const }
              : KIND[l.kind]
            return (
              <Fragment key={l.no}>
                <tr>
                  <td className="py-1.5 pr-3 text-gray-500">{l.no}</td>
                  <td className="py-1.5 pr-3 text-gray-900">{l.name}</td>
                  <td className="py-1.5 pr-3 text-gray-600">
                    {l.partNumber ?? <span className="text-gray-400">нет в заявке</span>}
                    {l.matchedBy === 'UNF_CARD' && (
                      <div className="text-xs text-gray-400">по карточке УНФ</div>
                    )}
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">
                    <Change from={l.itemQuantity} to={l.quantity} fmt={qty} />
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">
                    <Change from={l.itemPrice} to={l.price} fmt={money} />
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{money(l.sum)}</td>
                  <td className="py-1.5 text-right">
                    <Badge color={badge.color}>{badge.label}</Badge>
                  </td>
                </tr>
                {editable && (
                  <tr>
                    <td />
                    <td colSpan={6} className="pb-2">
                      <UnfInvoiceLineChoice
                        choice={choices[l.no]}
                        linkable={candidate.linkable}
                        current={
                          (l.linked || guessed) && l.itemId
                            ? { itemId: l.itemId, partNumber: l.partNumber ?? '' }
                            : null
                        }
                        disabled={disabled}
                        onChange={(c) => onChoice(l.no, c)}
                      />
                    </td>
                  </tr>
                )}
              </Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
